import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="time-offset"
// Lets an admin enter a time offset rule's duration as an amount and a unit. The rule stores
// seconds, and the read-only seconds field is the only one the form submits. The server owns the
// units (each unit option's value is its size in seconds, from RulesHelper) and the wording (the
// pipeline_rules.time_offset_* locale strings), so this controller only multiplies and fills
// placeholders.
export default class extends Controller {
  static targets = ["field", "operator", "amount", "unit", "seconds", "preview"];
  static values = { windows: Object, previews: Object };

  connect() {
    // After Back, Turbo's page cache restores the edited amount, unit and seconds together, and
    // this controller connects again. Seconds follow the visible amount and unit, so the form
    // saves the duration it describes. On a fresh render amount times unit is the stored seconds
    // (time_offset_duration never rounds), so nothing changes there.
    this.durationChanged();
  }

  durationChanged() {
    const amount = this.amountTarget.value;
    this.secondsTarget.value = amount === "" ? "" : Number(amount) * Number(this.unitTarget.value);
    this.preview();
  }

  preview() {
    const field = this.fieldTarget.selectedOptions[0];
    const amountInput = this.amountTarget;

    // An invalid amount (negative, fractional) blocks submit, so it gets no sentence either.
    if (!field || field.value === "" || amountInput.value === "" || !amountInput.validity.valid) {
      this.previewTarget.textContent = "";
      return;
    }

    // The parsed number, not the typed text: "090" and "1e3" save as 90 and 1000.
    const amount = Number(amountInput.value);
    const seconds = Number(this.secondsTarget.value);
    const operator = this.operatorTarget.value;
    const zero = seconds === 0;
    const window = this.fill(this.windowsValue[zero ? `${operator}_zero` : operator], {
      duration: `${amount} ${this.unitName(amount)}`
    });

    this.previewTarget.textContent = this.fill(this.previewsValue[zero ? "zero" : operator], {
      timestamp_field: field.textContent.trim(),
      window,
      cutoff: new Date(Date.now() - seconds * 1000).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short"
      })
    });
  }

  unitName(amount) {
    const unit = this.unitTarget.selectedOptions[0];
    return amount === 1 ? unit.dataset.singular : unit.textContent;
  }

  // Fills the %{name} placeholders of a Rails locale string.
  fill(template, values) {
    return template.replace(/%\{(\w+)\}/g, (_match, name) => values[name]);
  }
}
